import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { countriesAPI, energyMetricsAPI, githubAPI, vedaonlineAPI } from '../services/api';
import toast from 'react-hot-toast';
import GenerationProfileChart from '../components/simulator/generationProfile/GenerationProfileChart';
import OverviewTab from '../components/tabs/countryDashboard/OverviewTab';
import RenewablePotentialTab from '../components/tabs/countryDashboard/RenewablePotentialTab';
import TransmissionLineTab from '../components/tabs/countryDashboard/TransmissionLineTab';
import DemandFuelPriceTab from '../components/tabs/countryDashboard/DemandFuelPriceTab';
import ExistingStockTab from '../components/tabs/countryDashboard/ExistingStockTab';
import ResultsTab from '../components/tabs/countryDashboard/ResultsTab';
import WhatVervestacksTab from '../components/tabs/countryDashboard/WhatVervestacksTab';
import ChartCard from '../components/common/ChartCard';
import GlobalHeader from '../components/layout/GlobalHeader';
import CountryHeader from '../components/layout/CountryHeader';
import TabNavigation from '../components/layout/TabNavigation';
import DashboardTabPanel from '../components/layout/DashboardTabPanel';
import PresenterModal from '../components/modals/PresenterModal';
import { exitFullscreen } from '../utils/fullscreenApi';
import useCountryDashboardChartHeight from '../hooks/useCountryDashboardChartHeight';
import {
  VALID_COUNTRY_DASHBOARD_TABS,
  DEFAULT_GITHUB_LINKS,
  COUNTRY_CARD_BACKGROUND,
  getCountryDashboardTabs,
} from '../constants/countryDashboard';

const vedaModelTabKey = (m) =>
  `${m?.model_id ?? ''}:${m?.repository_branch_name ?? ''}`;

const isSelectedModelInList = (selected, models) =>
  !!selected &&
  Array.isArray(models) &&
  models.some((m) => vedaModelTabKey(m) === vedaModelTabKey(selected));

const CountryDashboard = () => {
  const { isoCode, tab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [country, setCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSimulatorMode =
    location.pathname.endsWith('/simulator') ||
    location.pathname.endsWith('/simulator/') ||
    tab === 'simulator';

  const activeTab =
    tab && VALID_COUNTRY_DASHBOARD_TABS.includes(tab) ? tab : 'overview';

  const [existingStockData, setExistingStockData] = useState(null);
  const [existingStockLoading, setExistingStockLoading] = useState(false);
  const [existingStockError, setExistingStockError] = useState(null);

  const [vedaModels, setVedaModels] = useState([]);
  const [vedaModelsLoading, setVedaModelsLoading] = useState(false);
  const [vedaModelsError, setVedaModelsError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelViews, setModelViews] = useState([]);
  const [modelViewsLoading, setModelViewsLoading] = useState(false);
  const [modelViewsError, setModelViewsError] = useState(null);

  const showHourlySimulator = isSimulatorMode;
  const [githubLinks, setGithubLinks] = useState(DEFAULT_GITHUB_LINKS);
  const globalHeaderRef = useRef(null);
  const countryHeaderRef = useRef(null);
  const tabNavigationRef = useRef(null);
  const [activePresenterView, setActivePresenterView] = useState(null);
  const activePresenterViewRef = useRef(null);
  const fullscreenHintShownForPresenterRef = useRef(false);
  const generationProfileChartRef = useRef(null);
  const lastDashboardTabRef = useRef('overview');
  /** Tabs that stay mounted after first visit (per country session; reset on isoCode change). */
  const [mountedTabIds, setMountedTabIds] = useState(['overview']);
  const existingStockLoadedIsoRef = useRef(null);
  const vedaModelsLoadedIsoRef = useRef(null);
  const modelViewsLoadedKeyRef = useRef(null);
  const pendingModelViewsKeyRef = useRef(null);
  const pendingVedaModelsIsoRef = useRef(null);

  const chartHeight = useCountryDashboardChartHeight({
    activeTab,
    showHourlySimulator,
    country,
    globalHeaderRef,
    countryHeaderRef,
    tabNavigationRef,
  });

  const tabs = useMemo(
    () => getCountryDashboardTabs(country?.name),
    [country?.name]
  );

  useEffect(() => {
    activePresenterViewRef.current = activePresenterView;
  }, [activePresenterView]);

  useEffect(() => {
    fullscreenHintShownForPresenterRef.current = false;
  }, [activePresenterView?.id]);

  const loadCountryData = useCallback(async () => {
    try {
      const data = await countriesAPI.getByIso(isoCode);
      setCountry(data.country);
    } catch (error) {
      toast.error(`Failed to load data for ${isoCode}`);
      console.error('Error loading country:', error);
    } finally {
      setLoading(false);
    }
  }, [isoCode]);

  const loadCountriesList = useCallback(async () => {
    try {
      const data = await countriesAPI.getAll();
      setCountries(data.countries || []);
    } catch (error) {
      console.error('Error loading countries list:', error);
    }
  }, []);

  const loadVedaOnlineModels = useCallback(async (countryIso, options = {}) => {
    const { force = false } = options;
    const iso = (countryIso || '').toUpperCase();
    if (!iso) return;

    if (!force && vedaModelsLoadedIsoRef.current === iso) {
      return;
    }

    try {
      pendingVedaModelsIsoRef.current = iso;
      setVedaModelsLoading(true);
      setVedaModelsError(null);
      setSelectedModel(null);
      setModelViews([]);
      setModelViewsError(null);
      modelViewsLoadedKeyRef.current = null;
      pendingModelViewsKeyRef.current = null;

      const response = await vedaonlineAPI.getModelNames(iso);
      if (pendingVedaModelsIsoRef.current !== iso) {
        return;
      }
      setVedaModels(Array.isArray(response?.data) ? response.data : []);
      vedaModelsLoadedIsoRef.current = iso;
    } catch (error) {
      if (pendingVedaModelsIsoRef.current !== iso) {
        return;
      }
      const message =
        error?.response?.data?.error || error?.message || 'Failed to load VedaOnline models';
      setVedaModelsError(message);
    } finally {
      if (pendingVedaModelsIsoRef.current === iso) {
        setVedaModelsLoading(false);
      }
    }
  }, []);

  const loadModelViews = useCallback(async (model, options = {}) => {
    const { force = false } = options;
    if (!model || !model.repository_branch_name) return;
    const modelName = (model.name || '').trim();
    if (!modelName) return;

    const viewsKey = `${vedaModelsLoadedIsoRef.current || ''}:${modelName}`;
    if (!force && modelViewsLoadedKeyRef.current === viewsKey) {
      setSelectedModel(model);
      return;
    }

    try {
      pendingModelViewsKeyRef.current = viewsKey;
      setModelViewsLoading(true);
      setModelViewsError(null);
      setSelectedModel(model);
      const response = await vedaonlineAPI.getModelViews(modelName);
      if (pendingModelViewsKeyRef.current !== viewsKey) {
        return;
      }
      setModelViews(Array.isArray(response?.data) ? response.data : []);
      modelViewsLoadedKeyRef.current = viewsKey;
    } catch (error) {
      if (pendingModelViewsKeyRef.current !== viewsKey) {
        return;
      }
      const message =
        error?.response?.data?.error || error?.message || 'Failed to load model views';
      setModelViewsError(message);
    } finally {
      if (pendingModelViewsKeyRef.current === viewsKey) {
        setModelViewsLoading(false);
      }
    }
  }, []);

  const exitPageFullscreen = useCallback(() => {
    exitFullscreen();
  }, []);

  const closePresenterModal = useCallback(() => {
    exitPageFullscreen();
    setActivePresenterView(null);
  }, [exitPageFullscreen]);

  const loadExistingStockData = useCallback(async (countryIso, options = {}) => {
    const { force = false } = options;
    if (!countryIso) return;

    if (
      !force &&
      existingStockLoadedIsoRef.current === countryIso &&
      existingStockData
    ) {
      return;
    }

    try {
      setExistingStockLoading(true);
      setExistingStockError(null);
      const response = await energyMetricsAPI.getExistingStockMetrics(countryIso);

      if (response && response.success && response.data) {
        setExistingStockData(response.data);
        existingStockLoadedIsoRef.current = countryIso;
      } else {
        throw new Error('Failed to load existing stock data');
      }
    } catch (error) {
      setExistingStockError(error.message);
      toast.error('Failed to load existing stock data');
      console.error('Error loading existing stock data:', error);
    } finally {
      setExistingStockLoading(false);
    }
  }, [existingStockData]);

  const resetDashboardDataForCountryChange = useCallback(() => {
    existingStockLoadedIsoRef.current = null;
    vedaModelsLoadedIsoRef.current = null;
    modelViewsLoadedKeyRef.current = null;
    pendingModelViewsKeyRef.current = null;
    pendingVedaModelsIsoRef.current = null;
    setExistingStockData(null);
    setExistingStockError(null);
    setExistingStockLoading(false);
    setVedaModels([]);
    setVedaModelsError(null);
    setVedaModelsLoading(false);
    setSelectedModel(null);
    setModelViews([]);
    setModelViewsError(null);
    setModelViewsLoading(false);
  }, []);

  useEffect(() => {
    const seedTab =
      tab && VALID_COUNTRY_DASHBOARD_TABS.includes(tab) ? tab : 'overview';
    setMountedTabIds([seedTab]);
    resetDashboardDataForCountryChange();
    loadCountryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isoCode]);

  useEffect(() => {
    if (countries.length === 0) {
      loadCountriesList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isoCode) return;

    if (!isSimulatorMode && (!tab || !VALID_COUNTRY_DASHBOARD_TABS.includes(tab))) {
      navigate(`/country/${isoCode}/overview`, { replace: true });
    }
  }, [tab, isoCode, navigate, isSimulatorMode]);

  useEffect(() => {
    if (!isSimulatorMode && tab && VALID_COUNTRY_DASHBOARD_TABS.includes(tab)) {
      lastDashboardTabRef.current = tab;
    }
  }, [isSimulatorMode, tab]);

  useEffect(() => {
    if (showHourlySimulator) return;
    setMountedTabIds((prev) =>
      prev.includes(activeTab) ? prev : [...prev, activeTab]
    );
  }, [activeTab, showHourlySimulator]);

  useEffect(() => {
    if (showHourlySimulator) return;
    if (!mountedTabIds.includes('existing-stock')) return;
    if (!country?.iso_code) return;
    loadExistingStockData(country.iso_code);
  }, [showHourlySimulator, mountedTabIds, country?.iso_code, loadExistingStockData]);

  useEffect(() => {
    if (showHourlySimulator) return;
    if (!mountedTabIds.includes('results')) return;
    if (!isoCode) return;
    loadVedaOnlineModels(isoCode);
  }, [showHourlySimulator, mountedTabIds, isoCode, loadVedaOnlineModels]);

  // Auto-select first model when list arrives or country changes; keep user choice within same country.
  useEffect(() => {
    if (showHourlySimulator) return;
    if (!mountedTabIds.includes('results')) return;
    if (vedaModelsLoading || vedaModelsError) return;
    if (!vedaModels.length) return;

    const routeIso = (isoCode || '').toUpperCase();
    if (!routeIso || vedaModelsLoadedIsoRef.current !== routeIso) {
      return;
    }

    if (isSelectedModelInList(selectedModel, vedaModels)) {
      return;
    }

    const first = vedaModels[0];
    if (!first.repository_branch_name) return;
    if (!(first.name || '').trim()) return;
    loadModelViews(first);
  }, [
    showHourlySimulator,
    mountedTabIds,
    isoCode,
    vedaModelsLoading,
    vedaModelsError,
    vedaModels,
    selectedModel,
    loadModelViews,
  ]);

  useEffect(() => {
    let wasFs = !!(
      document.fullscreenElement ??
      document.webkitFullscreenElement ??
      document.mozFullScreenElement ??
      document.msFullscreenElement
    );
    const sync = () => {
      const d = document;
      const el =
        d.fullscreenElement ??
        d.webkitFullscreenElement ??
        d.mozFullScreenElement ??
        d.msFullscreenElement;
      const next = !!el;
      if (
        next &&
        !wasFs &&
        activePresenterViewRef.current &&
        !fullscreenHintShownForPresenterRef.current
      ) {
        fullscreenHintShownForPresenterRef.current = true;
        toast('Esc or Alt+Shift+E exits fullscreen. Alt+Shift+X closes the presenter.', {
          duration: 5000,
        });
      }
      wasFs = next;
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    document.addEventListener('mozfullscreenchange', sync);
    document.addEventListener('MSFullscreenChange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
      document.removeEventListener('mozfullscreenchange', sync);
      document.removeEventListener('MSFullscreenChange', sync);
    };
  }, []);

  useEffect(() => {
    if (!activePresenterView) return;
    const onKey = (e) => {
      if (e.altKey && e.shiftKey && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
        exitPageFullscreen();
      }
      if (e.altKey && e.shiftKey && (e.key === 'x' || e.key === 'X')) {
        e.preventDefault();
        closePresenterModal();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activePresenterView, exitPageFullscreen, closePresenterModal]);

  useEffect(() => {
    const resolveGithubLinks = async () => {
      const iso = (country?.iso_code || isoCode || '').toUpperCase();
      if (!iso) {
        setGithubLinks(DEFAULT_GITHUB_LINKS);
        return;
      }

      try {
        const result = await githubAPI.getBranches(iso);
        setGithubLinks(result);
      } catch (error) {
        if (error.response?.status === 429) {
          const resetTime = error.response.data?.resetTime;
          const retryAfter = error.response.data?.retryAfter;
          console.warn('GitHub API rate limit exceeded', { resetTime, retryAfter });
          toast.error(`GitHub API rate limit exceeded. Please try again later.`, {
            duration: 5000,
          });
        } else {
          console.error('Error fetching GitHub branches:', error);
        }
        setGithubLinks(DEFAULT_GITHUB_LINKS);
      }
    };

    resolveGithubLinks();
  }, [country?.iso_code, isoCode]);

  const handleTabChange = useCallback(
    (tabId) => {
      if (!isoCode) return;
      navigate(`/country/${isoCode}/${tabId}`);
    },
    [isoCode, navigate]
  );

  const returnToDashboardTab = useCallback(() => {
    if (!isoCode) return;
    const returnTab = lastDashboardTabRef.current || 'overview';
    navigate(`/country/${isoCode}/${returnTab}`);
  }, [isoCode, navigate]);

  const handleSimulatorToggle = useCallback(() => {
    if (!isoCode) return;

    if (showHourlySimulator) {
      returnToDashboardTab();
    } else {
      if (tab && VALID_COUNTRY_DASHBOARD_TABS.includes(tab)) {
        lastDashboardTabRef.current = tab;
      }
      navigate(`/country/${isoCode}/simulator`);
    }
  }, [isoCode, showHourlySimulator, navigate, tab, returnToDashboardTab]);

  const handleCountrySelect = useCallback(
    (selectedCountry) => {
      const targetUrl = showHourlySimulator
        ? `/country/${selectedCountry.iso_code}/simulator`
        : `/country/${selectedCountry.iso_code}/${activeTab}`;
      navigate(targetUrl);
    },
    [navigate, activeTab, showHourlySimulator]
  );

  const handleUnderstandingProfilesClick = useCallback(() => {
    generationProfileChartRef.current?.openFirstAccordion?.();
  }, []);

  const OverviewTabComponent = useMemo(() => {
    if (!country) return null;
    return (
      <OverviewTab country={country} chartHeight={chartHeight} ChartCard={ChartCard} />
    );
  }, [country, chartHeight]);

  const ExistingStockTabComponent = useMemo(() => {
    if (!country) return null;
    return (
      <ExistingStockTab
        country={country}
        existingStockLoading={existingStockLoading}
        existingStockError={existingStockError}
        existingStockData={existingStockData}
        onRetry={(countryIso) => loadExistingStockData(countryIso, { force: true })}
        ChartCard={ChartCard}
      />
    );
  }, [
    country,
    existingStockLoading,
    existingStockError,
    existingStockData,
    loadExistingStockData,
  ]);

  const GenerationProfileTab = useMemo(() => {
    if (!country) return null;
    return (
      <GenerationProfileChart
        key={`simulator-${country.iso_code}-${location.search}`}
        ref={generationProfileChartRef}
        countryIso={country.iso_code}
      />
    );
  }, [country, location.search]);

  const mapLayoutActive =
    !showHourlySimulator && activeTab === 'renewable-potential';
  const transmissionLayoutActive =
    !showHourlySimulator && activeTab === 'transmission-line';

  const renewableCountryIso = (isoCode || country?.iso_code || '').toUpperCase();
  const transmissionCountryIso = (isoCode || country?.iso_code || '').toUpperCase();

  const RenewablePotentialTabComponent = useMemo(() => {
    if (!renewableCountryIso) return null;
    return (
      <RenewablePotentialTab
        countryIso={renewableCountryIso}
        isTabActive={mapLayoutActive}
      />
    );
  }, [renewableCountryIso, mapLayoutActive]);

  const TransmissionLineTabComponent = useMemo(() => {
    if (!transmissionCountryIso) return null;
    return (
      <TransmissionLineTab
        countryIso={transmissionCountryIso}
        isTabActive={transmissionLayoutActive}
      />
    );
  }, [transmissionCountryIso, transmissionLayoutActive]);

  const DemandFuelPriceTabComponent = useMemo(() => {
    if (!country) return null;
    return <DemandFuelPriceTab country={country} />;
  }, [country]);

  const ResultsTabComponent = useMemo(() => {
    if (!country) return null;
    return (
      <ResultsTab
        vedaModels={vedaModels}
        vedaModelsLoading={vedaModelsLoading}
        vedaModelsError={vedaModelsError}
        loadModelViews={loadModelViews}
        selectedModel={selectedModel}
        modelViews={modelViews}
        modelViewsLoading={modelViewsLoading}
        modelViewsError={modelViewsError}
        setActivePresenterView={setActivePresenterView}
      />
    );
  }, [
    country,
    vedaModels,
    vedaModelsLoading,
    vedaModelsError,
    loadModelViews,
    selectedModel,
    modelViews,
    modelViewsLoading,
    modelViewsError,
  ]);

  const WhatVervestacksTabComponent = useMemo(() => {
    if (!country) return null;
    return <WhatVervestacksTab country={country} />;
  }, [country]);

  const dashboardTabPanels = useMemo(
    () => ({
      overview: OverviewTabComponent,
      'existing-stock': ExistingStockTabComponent,
      'renewable-potential': RenewablePotentialTabComponent,
      'transmission-line': TransmissionLineTabComponent,
      'demand-fuel-price': DemandFuelPriceTabComponent,
      results: ResultsTabComponent,
      'what-vervestacks': WhatVervestacksTabComponent,
    }),
    [
      OverviewTabComponent,
      ExistingStockTabComponent,
      RenewablePotentialTabComponent,
      TransmissionLineTabComponent,
      DemandFuelPriceTabComponent,
      ResultsTabComponent,
      WhatVervestacksTabComponent,
    ]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center text-gray-700">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p>Loading {isoCode} dashboard...</p>
        </div>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center text-gray-700">
          <h1 className="text-2xl font-bold mb-4">Country Not Found</h1>
          <p className="mb-6">The country with ISO code "{isoCode}" could not be found.</p>
          <Link to="/" className="btn-primary">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vervestacks-secondary relative">
      <GlobalHeader
        ref={globalHeaderRef}
        countryIsoCode={country?.iso_code}
        isoCode={isoCode}
        githubLinks={githubLinks}
        showUnderstandingEnergyProfiles={showHourlySimulator}
        onUnderstandingEnergyProfilesClick={handleUnderstandingProfilesClick}
      />

      <PresenterModal
        activePresenterView={activePresenterView}
        onClose={closePresenterModal}
      />

      <div className="bg-vervestacks-secondary px-2 sm:px-4 lg:px-8 pb-3 sm:pb-4">
        <div className="rounded-xl shadow-card" style={{ backgroundColor: COUNTRY_CARD_BACKGROUND }}>
          <CountryHeader
            ref={countryHeaderRef}
            country={country}
            countries={countries}
            showHourlySimulator={showHourlySimulator}
            onUnderstandingProfilesClick={handleUnderstandingProfilesClick}
            onCountrySelect={handleCountrySelect}
            onSimulatorToggle={handleSimulatorToggle}
          />

          {!showHourlySimulator && (
            <TabNavigation
              ref={tabNavigationRef}
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          )}

          <div className={`flex-1 min-w-0 ${showHourlySimulator ? 'overflow-hidden' : ''}`}>
            {/* Keep dashboard tabs mounted while simulator is open so map/data state survives */}
            <div
              key={isoCode}
              className={showHourlySimulator ? 'hidden' : 'relative block min-w-0'}
              aria-hidden={showHourlySimulator}
            >
              {mountedTabIds.map((tabId) => (
                <DashboardTabPanel
                  key={tabId}
                  tabId={tabId}
                  activeTab={activeTab}
                >
                  {dashboardTabPanels[tabId]}
                </DashboardTabPanel>
              ))}
            </div>
            {showHourlySimulator ? (
              <motion.div
                key="hourly-simulator"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {GenerationProfileTab}
              </motion.div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryDashboard;
